/**
 * Toggles gulv-beregner vs stk-antal for variants containing "sokkel" in the option "Størrelse".
 *
 * Relies on the theme event: `on:variant:change` (dispatched by `variant-picker`).
 */
(function () {
  const OFFER_TEXT_FALLBACK = "Få et uforpligtende tilbud";
  const BIND_FLAG = "sokkelToggleBound";

  function normalize(str) {
    return String(str || "")
      .trim()
      .toLowerCase();
  }

  function getSizeOptionIndex(product) {
    const optionNames = product && Array.isArray(product.options) ? product.options : [];
    return optionNames.findIndex((name) => normalize(name).includes("størrelse"));
  }

  function isSokkelVariant(detail) {
    const variant = detail && detail.variant;
    const product = detail && detail.product;
    if (!variant || !product) return false;

    const sizeIndex = getSizeOptionIndex(product);
    if (sizeIndex < 0) return false;

    const selectedValue = Array.isArray(variant.options) ? variant.options[sizeIndex] : "";
    return normalize(selectedValue).includes("sokkel");
  }

  function setDisabledWithin(root, disabled) {
    if (!root) return;
    root.querySelectorAll("input, select, textarea, button").forEach((el) => {
      // Don't disable the submit button / variant radios
      if (el && el.name === "add") return;
      if (el && el.type === "radio") return;
      el.disabled = disabled;
    });
  }

  function toggle(root, detail) {
    if (!root) return;

    const variantId = detail && detail.variant && detail.variant.id;
    if (variantId && root.dataset.sokkelToggleVariantId === String(variantId)) return;
    if (variantId) root.dataset.sokkelToggleVariantId = String(variantId);

    // Store current variant price (cents) for calculator price updates.
    const variant = detail && detail.variant;
    if (variant && typeof variant.price === "number") {
      root.dataset.gulvCalculatorVariantPrice = String(variant.price);
    } else {
      delete root.dataset.gulvCalculatorVariantPrice;
    }

    const sokkel = isSokkelVariant(detail);

    const calculator = root.querySelector("[data-gulv-calculator]");
    if (calculator) {
      calculator.hidden = sokkel;
      setDisabledWithin(calculator, sokkel);
    }

    const qtyGulv = root.querySelector("[data-qty-gulv]");
    const qtyStk = root.querySelector("[data-qty-stk]");
    const qtyHidden = root.querySelector('input[data-qty-hidden][name="quantity"]');

    if (qtyGulv) {
      qtyGulv.hidden = sokkel;
      setDisabledWithin(qtyGulv, sokkel);
    }

    if (qtyStk) {
      qtyStk.hidden = !sokkel;
      setDisabledWithin(qtyStk, !sokkel);

      // Ensure stk quantity has a sane default when it becomes active.
      if (sokkel) {
        const input = qtyStk.querySelector('input[name="quantity"]');
        if (input && (!input.value || Number(input.value) < 1)) input.value = "1";
      }
    }

    // Hidden qty is only for cases where no selector is visible.
    if (qtyHidden) {
      // If we render both gulv & stk selectors, keep hidden qty disabled.
      qtyHidden.disabled = true;
    }

    const form = detail && detail.form;
    const addBtn = form ? form.querySelector('[name="add"]') : root.querySelector('[name="add"]');
    if (addBtn) {
      const offerText = addBtn.dataset.offerText || OFFER_TEXT_FALLBACK;

      // Only override when variant is available; if sold out, keep theme strings.
      if (sokkel && variant && variant.available) {
        addBtn.textContent = offerText;
      } else if (!sokkel && variant && variant.available) {
        // Restore default label from theme dataset.
        addBtn.textContent = addBtn.dataset.addToCartText || addBtn.textContent;
      }
    }

    // Keep calculator outputs in sync when it becomes visible again.
    if (!sokkel) {
      updateGulvCalculatorFromDom(root);
    }
  }

  function parseDanishMoneyToNumber(str) {
    // e.g. "1.234,50 kr" -> 1234.5
    const cleaned = String(str || "")
      .replace(/\s*kr\s*/i, "")
      .replace(/\./g, "")
      .replace(",", ".")
      .trim();
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }

  function getVariantPriceNumber(root) {
    const raw = root && root.dataset && root.dataset.gulvCalculatorVariantPrice;
    const cents = raw != null ? Number(raw) : NaN;
    if (Number.isFinite(cents) && cents > 0) return cents / 100;

    // Fallback to price shown in the normal price block.
    const backend = root ? root.querySelector("#backendprice") : null;
    if (backend) {
      const parsed = parseDanishMoneyToNumber(backend.textContent);
      if (parsed != null) return parsed;
    }
    return null;
  }

  function updateGulvCalculatorFromDom(root) {
    if (!root) return;

    const calculator = root.querySelector("[data-gulv-calculator]");
    if (!calculator || calculator.hidden) return;

    const sqmFloor = calculator.querySelector("#sqm-floor");
    const packageAmount = calculator.querySelector("#package-amount");
    const packageTotal = calculator.querySelector("#total-amount-of-packages");
    const pricePackageOutput = calculator.querySelector("#price-package");
    const amountSqm = calculator.querySelector("#total-sqm");
    const amountPackages = calculator.querySelector("#amountpackages");
    const totalAmountFloor = root.querySelector(".qty-input__input.input.gulvinput");

    if (!sqmFloor || !packageAmount || !packageTotal || !amountSqm || !totalAmountFloor) return;

    const sqmValue = Number.parseFloat(sqmFloor.value);
    const packageValue = Number.parseFloat(packageAmount.value);
    if (!Number.isFinite(sqmValue) || !Number.isFinite(packageValue) || packageValue === 0) return;

    const amountPackagesText = amountPackages ? amountPackages.innerText.replace(/r$/, "") : "Pakke";
    const totalAmountCalcRoundedUp = Math.max(1, Math.ceil(sqmValue / packageValue));

    if (amountPackages) {
      amountPackages.innerText = totalAmountCalcRoundedUp > 1 ? amountPackagesText + "r" : amountPackagesText;
    }

    totalAmountFloor.value = String(totalAmountCalcRoundedUp);
    packageTotal.innerHTML = String(totalAmountCalcRoundedUp);

    const packagePrice = getVariantPriceNumber(root);
    if (pricePackageOutput && packagePrice != null) {
      const totalPriceCalc = totalAmountCalcRoundedUp * packagePrice;
      const totalPriceFormatted = totalPriceCalc.toLocaleString("da-DK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      pricePackageOutput.innerHTML = totalPriceFormatted + " kr";
    }

    const amountSqmCalc = (packageValue * totalAmountCalcRoundedUp).toFixed(2).replace(".", ",");
    amountSqm.innerHTML = amountSqmCalc + " m<sup>2</sup>";
  }

  function bindGulvCalculatorButtons(productRoot) {
    if (!productRoot) return;
    const calculator = productRoot.querySelector("[data-gulv-calculator]");
    if (!calculator) return;

    if (calculator.dataset.sokkelCalculatorButtonsBound === "true") return;
    calculator.dataset.sokkelCalculatorButtonsBound = "true";

    // Keep calculations updated when user types.
    const sqmFloor = calculator.querySelector("#sqm-floor");
    if (sqmFloor) {
      sqmFloor.addEventListener("input", () => updateGulvCalculatorFromDom(productRoot));
      sqmFloor.addEventListener("change", () => updateGulvCalculatorFromDom(productRoot));
    }

    // Handle +/- clicks (capture phase) to avoid competing global handlers.
    calculator.addEventListener(
      "click",
      (evt) => {
        const target = evt.target;
        const btn = target && target.closest ? target.closest(".wrapper.gulv .input-button") : null;
        if (!btn || !calculator.contains(btn)) return;

        evt.preventDefault();
        evt.stopPropagation();

        const wrapper = btn.closest(".wrapper.gulv");
        const input = wrapper ? wrapper.querySelector('input[type="number"]') : null;
        if (!input) return;

        let current = Number.parseFloat(input.value);
        if (!Number.isFinite(current) || current < 1) current = 1;

        if (btn.classList.contains("add")) {
          current += 1;
        } else if (btn.classList.contains("remove")) {
          current = Math.max(1, current - 1);
        }

        input.value = String(current);
        updateGulvCalculatorFromDom(productRoot);
      },
      true
    );
  }

  function getInitialDetailFromDom(productRoot) {
    const variantPicker = productRoot.querySelector("variant-picker");
    const idInput = productRoot.querySelector('.js-product-form input[name="id"]');
    if (!variantPicker || !idInput) return null;

    const dataEl = variantPicker.querySelector('[type="application/json"]');
    if (!dataEl) return null;

    let product;
    try {
      product = JSON.parse(dataEl.textContent).product;
    } catch {
      return null;
    }

    const variantId = Number(idInput.value);
    const variant = product && Array.isArray(product.variants) ? product.variants.find((v) => Number(v.id) === variantId) : null;

    return { form: productRoot.querySelector(".js-product-form"), product, variant };
  }

  function handleVariantChangeEvent(event) {
    const detail = (event && event.detail) || {};
    const form = detail.form;
    const productRoot = form ? form.closest(".js-product") : null;
    if (!productRoot) return;

    if (!productRoot.querySelector("[data-gulv-calculator]") && !productRoot.querySelector("[data-qty-gulv]")) return;
    toggle(productRoot, detail);
  }

  // Theme documentation suggests listening on `document`, but some setups work more reliably
  // when also binding directly to the `variant-picker` element.
  document.addEventListener("on:variant:change", handleVariantChangeEvent);
  window.addEventListener("on:variant:change", handleVariantChangeEvent);

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      document.querySelectorAll(".js-product").forEach((productRoot) => {
        if (!productRoot.querySelector("[data-gulv-calculator]") && !productRoot.querySelector("[data-qty-gulv]")) return;

        bindGulvCalculatorButtons(productRoot);

        const variantPicker = productRoot.querySelector("variant-picker");
        if (variantPicker && !variantPicker.dataset[BIND_FLAG]) {
          variantPicker.addEventListener("on:variant:change", handleVariantChangeEvent);
          variantPicker.dataset[BIND_FLAG] = "true";
        }

        const detail = getInitialDetailFromDom(productRoot);
        if (detail) {
          toggle(productRoot, detail);
          updateGulvCalculatorFromDom(productRoot);
        }
      });
    },
    { once: true }
  );
})();
